"use client"
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';
import { usePathname, useRouter } from 'next/navigation';
import OtpInput from 'react-otp-input';
import Cookies from 'js-cookie';
import AuthBottom from '../AuthBottom';
import AuthTop from '../AuthTop';
import usePost from '@/Utils/Hooks/usePost'
import { ForgotPasswordAPI, VerifyOtpAPI } from '@/Utils/APIRequest/Endpoints'
import PrimaryButton from '@/Component/Common/Forms/PrimaryButton';
import { LoaderSVG } from '@/Component/Common/SVGs/CommonSVG';
import FormLabel from '@/Component/Common/Forms/FormLabel';
import { useTranslation } from '@/Utils/Hooks/useTranslate';
import { useLoading } from '@/Utils/Hooks/useLoading';
import SkeletonLoader from '@/Layout/SkeletonLoader';
import { getUserDetails } from '@/lib/Features/UserDetails/userDetails';
import axios from 'axios';
import { CustomToast } from '@/Utils/CustomToast';
import { ImageLoaderSVG } from '@/Component/Common/SVGs/AuthSVG';

const clearAuthEmailCookies = () => {
    Cookies.remove('register_email');
    Cookies.remove('admin_email');
    Cookies.remove('forgot_email');
};

const VerifyOTP = () => {
    const router = useRouter();
    const dispatch = useDispatch();
    const { translations, locale } = useTranslation();
    const { skeletonLoading, startSkeletonLoading, stopSkeletonLoading } = useLoading();
    const [otp, setOtp] = useState('');
    const [getCookieEmail, setGetCookieEmail] = useState<any>(null);
    const [getForgotCookieEmail, setGetForgotCookieEmail] = useState<any>(null);
    const [timeLeft, setTimeLeft] = useState(120);
    const [isResendDisabled, setIsResendDisabled] = useState(true);
    const pathname = usePathname();
    const roleFromUrl = pathname.split('/')[2];
    const otpCompletedRef = useRef(false);

    const translatedRole = roleFromUrl === 'brand' ? translations.brand : translations.influencer

    const tempUrl = getForgotCookieEmail ? '/reset-password' : `/register/${roleFromUrl}/steps`;
    const tempResendUrl = getForgotCookieEmail ? '/verify-otp' : `/register/${roleFromUrl}/verify-otp`;

    const { data, isPending, mutate } = usePost(VerifyOtpAPI, tempUrl, async (response) => {
        if (response?.status === 200 || response?.status === 201) {
            let userDetail = {
                role: response?.data?.role,
                pending_steps: response?.data?.pending_steps
            }
            Cookies.set('user_info', JSON.stringify(userDetail));
            Cookies.set('verifyUAT', response?.data?.access_token);
            dispatch(getUserDetails(data?.data))
            otpCompletedRef.current = true;
            clearAuthEmailCookies();

            if (response?.data?.role !== 'admin') {

                // Create Comet Chat Users
                let obj = {
                    uid: response?.data?.id,
                    name: response?.data?.role === 'influencer' ? response?.data?.display_name : `${response?.data?.first_name} ${response?.data?.last_name}`,
                    withAuthToken: true
                }
                if (response?.data?.image) {
                    obj['avatar'] = response?.data?.image
                }
                const createUser = await axios.post('/api/chat', { data: obj, endpoint: "users" })

                if (createUser?.data?.data?.message) {
                    // Need to do login
                    console.log("createUsercreateUser if", createUser?.data?.data?.message);
                } else {
                    // Created Successfully
                    let chatAuthToken = createUser?.data?.data?.authToken
                }
            }
        }
    }, null)

    const { data: resendData, isPending: resetPending, mutate: resendMutate } = usePost(ForgotPasswordAPI, null, null, null);

    useEffect(() => {
        const isAdminVerify = pathname === '/verify-otp';
        const forgotCookie = Cookies.get('forgot_email');
        // Prefer the cookie that matches this screen so a leftover register_email
        // cannot override admin_email (and vice versa).
        const tempCookie = forgotCookie
            ? null
            : isAdminVerify
                ? Cookies.get('admin_email') || Cookies.get('register_email')
                : Cookies.get('register_email') || Cookies.get('admin_email');
        setGetCookieEmail(tempCookie);
        setGetForgotCookieEmail(forgotCookie);

        startSkeletonLoading();
        setTimeout(() => {
            stopSkeletonLoading();
        }, 1000);
    }, [pathname])

    // Leave verify without submitting → drop email cookies so login/admin OTP
    // does not reuse the previous register email. Skip Strict Mode remounts.
    useEffect(() => {
        return () => {
            if (otpCompletedRef.current) return;
            if (typeof window !== 'undefined' && window.location.pathname.includes('verify-otp')) {
                return;
            }
            clearAuthEmailCookies();
        };
    }, []);

    useEffect(() => {
        if (timeLeft > 0) {
            const timerId = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(timerId);
        } else {
            setIsResendDisabled(false);
        }
    }, [timeLeft]);

    const handleChange = (otp) => {
        setOtp(otp)
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const tempObj: any = {
            email: getForgotCookieEmail ? getForgotCookieEmail : getCookieEmail,
            otp: otp,
            language_code: locale
        }
        mutate(tempObj)
    }

    const handleResend = () => {

        const tempObj: any = {
            email: getForgotCookieEmail ? getForgotCookieEmail : getCookieEmail,
            language_code: locale
        }
        resendMutate(tempObj, {
            onSuccess: (response) => {
                if (response?.status === 200 || response?.status === 201) {
                    CustomToast("success", `${translations?.resendOTPSuccess}`);
                    setTimeLeft(120);
                    setIsResendDisabled(true);
                    setOtp('');
                }
            },
            onError: (error) => {
                CustomToast("error", `${translations?.resendOTPError}`);
            }
        });
    }

    const handleLeaveToLogin = () => {
        clearAuthEmailCookies();
    };

    return (
        <div className='mx-auto max-w-xl flex flex-col h-full'>
            {getForgotCookieEmail ? <AuthTop skeletonLoading={skeletonLoading} logoutBtn={false} translations={translations} /> : <AuthTop title={`${translations.registerAs} ${translatedRole}`} roleFromUrl={roleFromUrl} skeletonLoading={skeletonLoading} logoutBtn={false} translations={translations} />}
            <h1 className='font-semibold lg:text-4xl sm:text-3xl xs:text-2xl text-xl mb-1 !leading-tight'>{skeletonLoading ? <SkeletonLoader height={60} rows={1} /> : translations.verifyTitle}</h1>
            <p className='text-gray text-sm'>{skeletonLoading ? <SkeletonLoader width="276" height={24} rows={1} /> : <> {translations.verifySubTitle}: <br />{getForgotCookieEmail ? getForgotCookieEmail : getCookieEmail} </>}</p>
            <div className='flex-1'>

                <form onSubmit={handleSubmit} className='sm:mt-10 mt-8'>
                    <FormLabel title={translations.verifyLabel} skeletonLoading={skeletonLoading} />
                    <OtpInput
                        value={otp}
                        onChange={handleChange}
                        numInputs={6}
                        renderSeparator={false}
                        renderInput={(props) => <input {...props} />}
                        inputType={'number'}
                        containerStyle={'mb-5 grid grid-cols-6 xs:gap-3 gap-1'}
                        inputStyle={'!w-full aspect-square text-2xl shadow-[0_1px_4px_rgba(0,0,0,0.04)] border border-neutral-200 font-medium rounded-xl w-full py-1 px-1 text-primary focus:outline-none focus:shadow-[0_0_0px_3px_rgba(165,208,255,1)] focus-visible:outline-none transition-shadow'}
                    />

                    {
                        isResendDisabled ?
                            <div className='flex items-center justify-end mb-5'>
                                {skeletonLoading ? <SkeletonLoader width="276" height={24} rows={1} /> :
                                    <>
                                        {/* <span className='text-base font-medium mr-2'>{`00:${timeLeft < 10 ? `0${timeLeft}` : timeLeft}`}</span> */}
                                        <span className='text-sm font-medium mr-2'>
                                            {`${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(2, '0')}`}
                                        </span>
                                        <span className='text-sm text-gray/80'>{translations.resendTimer}</span>
                                    </>
                                }
                            </div> :
                            <div className='mb-5 flex items-center justify-end'>
                                {skeletonLoading ? <SkeletonLoader width="276" height={24} rows={1} /> :
                                    <span className="text-primary cursor-pointer font-semibold flex items-center gap-1.5 text-sm" onClick={handleResend}>
                                        {translations.resendOTP}
                                        {resetPending && <ImageLoaderSVG />}
                                    </span>
                                }
                            </div>
                    }

                    <PrimaryButton customClass='w-full flex items-center justify-center !py-3' buttonType='submit' disabled={isPending} skeletonLoading={skeletonLoading} width={'100%'}>
                        {translations.submit}
                        {isPending && <LoaderSVG customClass='ml-2.5' />}
                    </PrimaryButton>
                    <AuthBottom
                        title={translations.haveAccount}
                        link="/login"
                        linkText={translations.loginNow}
                        skeletonLoading={skeletonLoading}
                        onLinkClick={handleLeaveToLogin}
                    />
                </form>
            </div>
        </div>
    )
}

export default VerifyOTP
